// The rows of a refresh applied to MemoryDB, as refreshData (worker.js) does
// for the response of our own write and for a colleague's update-data. db,
// refManager and the draft store are injected: the parity harness applies a
// colleague's save exactly as the socket does.

export const toObjId = (data) => ({ [data.id]: data });

// The labels table and the flat list of their elements (labelsAll)
export function computeLabelsColors(labels) {
  const all = [];

  for (const id in labels) {
    const label = labels[id];
    label.elements.forEach((elm) => {
      elm.type = label.type;
      elm.parent = label.id;
      all.push(elm);
    });
  }

  return [
    ["labels", Object.values(labels)],
    ["labelsAll", all]
  ];
}

// One row ({ action, data }) of table. A row the draft keeps
// (draft.incoming: journaled in a draft, or held by an open form) is not
// applied: "kept". Otherwise "deleted" (the row and its refs removed) or
// "put" (written, its refs filled). A label also rewrites its elements in
// labelsAll. options: { colleague, sent, username } (draft.incoming).
export function applyRow(
  table,
  { action, data },
  { db, refManager, draft },
  options = {}
) {
  if (draft.incoming(table, { action, data }, options)) return "kept";

  if (action === "delete") {
    if (table === "labels") {
      const [, labelsAll] = computeLabelsColors(toObjId(data));
      const ids = labelsAll[1].map(({ id }) => id);
      db.deleteByQuery("labelsAll", (l) => ids.includes(l.id));
    }
    refManager.delete(table, data.id);
    db.delete(table, data.id);
    return "deleted";
  }

  if (table === "labels") {
    const [, labelsAll] = computeLabelsColors(toObjId(data));
    const ids = labelsAll[1].map(({ parent }) => parent);
    db.deleteByQuery("labelsAll", (l) => ids.includes(l.parent));
    db.bulkPut("labelsAll", labelsAll[1]);
  }
  db.put(table, data);
  refManager.fillBelongs(db.getDB(), [[data]], table);
  return "put";
}

// rows: Map table -> [{ action, data }] (the data2Refresh of the payload).
// record(table, action, data): told of every row first, applied or kept
// (the reload overlay of the worker). A delete changes the refs of rows
// that are not in the refresh (the server updates the refs.belongs of the
// rows the deleted one required, not what the client computes from it):
// one full recompute (refManager.refill) for all the deletes.
// Returns { removed }: a row was deleted.
export function applyRows(
  rows,
  deps,
  { colleague = false, sent, username, record } = {}
) {
  let removed = false;
  for (const [table, items] of rows.entries()) {
    for (const { action, data } of items) {
      record?.(table, action, data);
      const outcome = applyRow(table, { action, data }, deps, {
        colleague,
        sent,
        username
      });
      if (outcome === "deleted") removed = true;
    }
  }

  if (removed) deps.refManager.refill();
  return { removed };
}
